import React from 'react';
import { Helmet } from 'react-helmet-async';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { JsonLd } from './JsonLd';

/**
 * Platform-wide <head> manager.
 *
 * Sits at the App-root level so every page inherits a sane default
 * SEO + JSON-LD payload. Page-specific Helmets (Home, Privacy, blog
 * SSR templates) override the title/description as needed; react-
 * helmet-async merges nested Helmets predictably.
 *
 * What lives here vs. in `client/index.html`:
 *   - `client/index.html` carries the BAREST critical meta + JSON-LD
 *     so an AI crawler that doesn't run JS still sees the brand,
 *     description, and Organization schema.
 *   - This component layers on the DB-driven values (organization
 *     logo, social profiles, twitter handle, etc.) and the per-page
 *     overrides once React hydrates.
 *
 * The site_url is treated as the canonical apex even when this page
 * is being viewed from a custom domain — the platform marketing
 * surface is single-host by design.
 */

const PUBLIC_ORIGIN = import.meta.env.VITE_PUBLIC_ORIGIN || 'https://hitzlink.com';

export const PlatformHead: React.FC = () => {
  const { settings } = usePlatformSettings();

  const siteName = settings.site_name || 'HitzLink';
  const origin = (settings.site_url || PUBLIC_ORIGIN).replace(/\/$/, '');
  const title = settings.seo_title || `${siteName} - Professional Landing Page Builder`;
  const description =
    settings.seo_description ||
    'Build stunning landing pages in minutes with our drag & drop builder. No coding required.';
  const ogImage = settings.seo_og_image || `${origin}/og-default.png`;
  const twitterHandle = settings.twitter_handle
    ? settings.twitter_handle.startsWith('@')
      ? settings.twitter_handle
      : `@${settings.twitter_handle}`
    : '@hitzlink';

  // Organization JSON-LD — anchors every other schema's `publisher`
  // reference via the @id, so the graph collapses to one organization
  // node across the page even when multiple schemas are present.
  const organizationSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${origin}#organization`,
    name: settings.organization_name || siteName,
    url: origin,
    ...(settings.organization_logo_url || settings.logo_url
      ? {
          logo: {
            '@type': 'ImageObject',
            url: settings.organization_logo_url || settings.logo_url,
          },
        }
      : {}),
    ...(Array.isArray(settings.organization_same_as) && settings.organization_same_as.length
      ? { sameAs: settings.organization_same_as.filter(Boolean) }
      : {}),
  };

  // WebSite JSON-LD with SearchAction — enables the sitelinks search
  // box treatment on Google when the property has enough authority.
  const websiteSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}#website`,
    url: origin,
    name: siteName,
    inLanguage: 'th',
    publisher: { '@id': `${origin}#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${origin}/blog?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="application-name" content={siteName} />

        {/* Open Graph — the AI extraction crowd parses these reliably */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={origin} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={siteName} />
        <meta property="og:locale" content="th_TH" />
        <meta property="og:locale:alternate" content="en_US" />

        {/* Twitter Card — explicit even when og:* would cover most clients */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content={twitterHandle} />
        <meta name="twitter:creator" content={twitterHandle} />

        {/* hreflang — page-specific Helmets can override per URL but
            the apex covers the common case where Home lives at /. */}
        <link rel="alternate" hrefLang="th" href={origin} />
        <link rel="alternate" hrefLang="en" href={`${origin}?lang=en`} />
        <link rel="alternate" hrefLang="x-default" href={origin} />

        {/* Default robots — index, follow, with the modern rich-result
            preview directives bumped up. Page-level Helmets can flip
            this to noindex when needed. */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        {settings.favicon_url && (
          <link rel="icon" type="image/x-icon" href={settings.favicon_url} />
        )}
        {settings.favicon_url && (
          <link rel="shortcut icon" href={settings.favicon_url} />
        )}
        {settings.favicon_url && (
          <link rel="apple-touch-icon" href={settings.favicon_url} />
        )}
      </Helmet>

      {/* JSON-LD — Organization + WebSite are baseline schemas that
          every page in the platform inherits. Individual pages layer
          on Article / FAQPage / SoftwareApplication via their own
          <JsonLd> instances. */}
      <JsonLd data={organizationSchema} id="organization" />
      <JsonLd data={websiteSchema} id="website" />
    </>
  );
};
